import { MobileAppSubpage } from "./mobile.data"
import { createSimpleId, isEmailValid, unknownDateToDate } from "../helpers/helpers"
import { PublicEventData } from "./public.model"
import { CustomAnswer, CustomQuestion } from "./events.data"
import { BrandingData } from "./publish.data"
import { PublishInfo, PublishStatus } from "./share.data"
export type DeepLinkType = 'login'


export interface DictionaryNum<T> {
    [id: number]: T | undefined;
}
export declare abstract class Dictionary<T> implements DictionaryNum<T> {
    [id: string]: T | undefined;
}

export type AppNotificationType = 'standard' | 'linked'
export interface AppTimeInterface {
  pushTime?: number
  endTime?: number
  active?: boolean
}

export interface AppNotificationBase { // for use in showing notifications in the app, including real ones "sent" from Flutter, which will be shown as a pop up immediately.  ALL PROPS SHOULD BE OF TYPE STRING (or a subset of string)
  id: string
  appId: string // needed because notifications can show up with "other apps" open and also for topic name
  eventId: string // needed for editing permissions
  appTitle: string // out of sync issue but oh well
  title: string
  message: string
  linkType: MobileAppSubpage
  linkUrl?: string
}

export const AppNotificationBaseKeys: (keyof AppNotificationBase)[] = ['id', 'appId', 
'eventId', 'appTitle', 'title', 'message', 'linkType', 'linkUrl']

export interface AppNotification extends AppTimeInterface, AppNotificationBase {
  type: AppNotificationType
  timeZone: string
  sentAutomatically?: boolean
  sentManually?: boolean
}

export interface AppSurvey extends AppTimeInterface {
  id: string
  timeZone: string
  customQuestions: CustomQuestion[]
  closed?: boolean
  title: string
}

export interface SurveySubmission {
  id?: string
  customQuestions: { [id: string]: CustomAnswer }
  name: string
  email: string
}

export interface SurveyQueryData {
  appId: string
  surveyId: string
  eventId: string
}

export interface SurveySubmissionData extends SurveyQueryData {
  submission: SurveySubmission
}


export type SurveyOption = 'build' | 'preview' | 'schedule' | 'fillout'
export const SurveyOptionTabNames: { [SO in SurveyOption]: string } = { 
  build: 'Build',
  preview: 'Preview',
  schedule: 'Schedule',
  fillout: 'Fill out an evaluation form'
}
export const SurveyOptions = {} as { [SO in SurveyOption]: SO }
Object.keys(SurveyOptionTabNames).forEach(SO => SurveyOptions[SO] = SO)

export interface SurveySelectData {
  surveyId: string
  appId: string
}

export interface FakeUserData {
  lastLoad: number
  token: string
}

export interface TestingDevice {
  name: string
  id: string
}
export interface JoiAppInfo extends PublishInfo {
  id: string 
  passwordProtected?: boolean
  tempPasswordProtected?: boolean
  nameAndEmail?: boolean
  tempNameAndEmail?: boolean
  version: number
  notifications: { [id: string]: AppNotification }
  notificationIds: string[]
  surveys:  { [id: string]: AppSurvey }
  surveyIds: string[]

  password?: string
  tempPassword?: string
  
  testingDevicePassword?: string
  fakeUsers: { [id: string]: FakeUserData }
  testingDevices?: TestingDevice[]
}

export const privateAppInfoProps: (keyof JoiAppInfo)[] = ['password', 'tempPassword', 'testingDevices', 'testingDevicePassword', 'fakeUsers' ]

export interface JoiAppData  {
  id?: string // only return when password not needed or is provided
  publishCode: string
  title: string
  completedSurveyIds?: string[]
  archivedNotificationIds?: string[]
  selectedSessionIds?: string[]
  passwordProtected?: boolean
  nameAndEmail?: boolean
  name?: string
  email?: string
}

export const canGoStraightToApp = (appInfo: JoiAppData): boolean => {
  if (!appInfo) return false
  const { id, nameAndEmail, name, email } = appInfo
  return !!id && (!nameAndEmail || (!!name && isEmailValid(email)))
}
  

export interface JoiApp {
  id: string
  eventId: string
  brandingData: BrandingData
  eventData: PublicEventData
}

export interface DownloadAppQuery {
  id: string
  eventId: string
}

export const standardizeAppInfo = (appInfo: JoiAppInfo): void => {
  appInfo.createdAt = unknownDateToDate(appInfo.createdAt)
  appInfo.publishedAt = unknownDateToDate(appInfo.publishedAt)
  if (!appInfo.testingDevices) appInfo.testingDevices = []
  if (!appInfo.fakeUsers) appInfo.fakeUsers = {}
  if (!appInfo.testingDevicePassword) appInfo.testingDevicePassword = createSimpleId()
}

export const appStatusToText: { [key in PublishStatus]: string } = { 'published': 'Active', 'never published': 'Not Active', 'unpublished': 'Not Active' }

export const codifyTitle = (title: string) => !title ? "" : title.toLowerCase().split(" ").join("")


export interface TextToggleData { // for use with program-menu as an input
  left: string
  right: string
  leftSelected: boolean
  toggleNotifier: Function
  loaded: boolean
}
